
import "leaflet/dist/leaflet.css";
import { debounce } from "lodash";
import { LatLngLiteral, Map } from "leaflet";
import { defineComponent, PropType } from "vue";
import { DataStation } from "@/types/datastation.type";
import { errorHandlerDefault } from "@/utils/handler.util";
import MarkerCluster from "@/components/MarkerCluster.component.vue";
import { getStationCoordinates } from "@/service/datastation.service";
import { LMap, LMarker, LTileLayer, LTooltip } from "@vue-leaflet/vue-leaflet";
import { ElMessage } from "element-plus";
import { GPSToLatLon } from "@/utils/coordinates.util";

interface StationMap extends DataStation {
  latlon?: [number, number];
}

const defPosition: LatLngLiteral = { lat: 40.41297, lng: -3.70296 };
export default defineComponent({
  props: {
    stationId: {
      type: Number,
      required: false,
    },
    detailRouterName: {
      type: String,
    },
    onClick: {
      type: Function,
      required: false,
    },
    datastationData: {
      type: Object as PropType<DataStation>,
    },
  },
  components: {
    LMap,
    LMarker,
    LTileLayer,
    LTooltip,
    MarkerCluster,
  },
  setup() {
    const firstPosition = defPosition;
    const dataReady = false;
    const mapOptions = { attributionControl: false };

    return {
      defPosition,
      firstPosition,
      dataReady,
      mapOptions,
    };
  },
  async created() {
    const response = await getStationCoordinates();
    this.stations = response.data;
    // - apply delay to search
    this.search = debounce(() => {
      this.refreshData().catch(errorHandlerDefault);
    }, 300);
    // - init data
    await this.refreshData();
  },

  data() {
    return {
      url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      zoom: 6,
      leafletReady: false,
      leafletObject: {} as Map,
      stations: [] as StationMap[],
      query: "" as string,
    };
  },
  methods: {
    search(): void {
      this.refreshData();
    },
    async onLeafletReady() {
      await this.$nextTick();
      this.leafletObject = (this.$refs.map as InstanceType<typeof LMap>).leafletObject;
      this.leafletReady = true;

      this.dataReady = true;
    },
    async refreshData() {
      // - append position
      this.stations.forEach((_) => {
        if (_.id && _.station && _.measurement) {
          let latlon = _.measurement.split(" ", 4);
          let x = +latlon[0];
          let cardinalPointX = latlon[1];
          let y = +latlon[2];
          let cardinalPointY = latlon[3];

          const data = GPSToLatLon(x, cardinalPointX, y, cardinalPointY);
          _.latlon = [data[0], data[1]];
        }
      });

      if (this.stations.length !== 0) {
        // - set first position
        this.firstPosition = this.getFirstPosition();

        // - move if leaflet is ready
        // if (this.leafletReady) this.flyTo(this.firstPosition, this.dataReady);
      } else {
        ElMessage.info(this.$t("info.stationsNotFound"));
      }

      this.dataReady = true;
    },

    /**
     * get first position
     * @returns
     */
    getFirstPosition(): LatLngLiteral {
      return this.stations[0]?.latlon
        ? { lat: this.stations[0].latlon[0], lng: this.stations[0].latlon[1] }
        : this.firstPosition;
    },

    /**
     * move map viewport
     * @param position
     * @param animate
     */
    flyTo(position: LatLngLiteral, animate = true) {
      this.leafletObject.flyTo(position, this.leafletObject.getZoom(), { animate });
    },

    openStationDetail(datastation: DataStation) {
      if (!this.$route.path.includes("stations/stations")) {
        this.$router.push(`/stations/stations/${datastation.station}`);
      } else {
        this.$router.push(`stations/${datastation.station}`);
      }
    },
  },
});
